import type { ReactNode } from 'react';

import { Icon, type IconName } from './Icon';

interface EmptyStateProps {
  /** Das Sinnbild ueber dem Text. Es sagt nichts Eigenes -- siehe unten. */
  icon?: IconName;
  /** Was hier fehlt, in einem Satz. */
  title: string;
  /** Warum es fehlt und was zu tun ist. Wahlfrei, aber fast immer richtig. */
  hint?: string;
  /** Die Handlung, die den Zustand beendet -- anlegen, Filter loeschen. */
  action?: ReactNode;
}

/**
 * Der Leerzustand einer Liste oder eines Bereichs.
 *
 * Ein leerer Bereich mit dem Wort "Keine Eintraege" laesst offen, ob nichts
 * da ist, ob der Filter zu eng steht oder ob etwas schiefging. Deshalb drei
 * Teile: Was fehlt, warum -- und der Knopf, der es beendet.
 *
 * **Der Unterschied zwischen "noch nichts" und "nichts gefunden" gehoert in
 * den Text.** Wer zum ersten Mal in eine leere Ticketliste sieht, braucht
 * "Legen Sie das erste Ticket an". Wer gerade nach "Drucker" gesucht hat,
 * braucht "Keine Treffer -- Suche zuruecksetzen". Dasselbe Bauteil, zwei
 * verschiedene Saetze; die Seite entscheidet, welcher.
 *
 * Das Sinnbild ist ``aria-hidden`` und traegt keine Aussage: Es gibt der
 * leeren Flaeche einen Mittelpunkt, damit sie nicht wie ein Ladefehler
 * aussieht.
 */
export function EmptyState({ icon, title, hint, action }: EmptyStateProps) {
  return (
    <div className="empty-state">
      {icon ? (
        <span className="empty-state__figure">
          <Icon name={icon} />
        </span>
      ) : null}
      <p className="empty-state__title">{title}</p>
      {hint ? <p className="empty-state__hint">{hint}</p> : null}
      {action ? <div className="empty-state__action">{action}</div> : null}
    </div>
  );
}
